import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import colors from 'src/integration/styles/colors';
import fonts from 'src/integration/styles/fonts';
import Icon from 'src/presentation/components/ui/Icon';
import { iconArrowDown } from 'src/integration/assets/AssetsProvider';
import { Option } from 'src/integration/types/Option';

interface ComponentProps {
  options: Option[];
  selectedId: Nullable<number>;
  onChange?: (id: Nullable<number>) => void;
}

const Wrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const Button = styled.button`
    ${() => css`
      font-family: ${fonts.fontFamily.headings};
      font-size: 18px;
      font-weight: 600;
      line-height: 150%;
      color: ${colors.primaryDarkJungleGreen};
      background-color: ${colors.primaryWhite};
      border: 1px solid ${colors.grey4};
      border-radius: 100px;
      padding: 10px 24px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-width: 200px;
      gap: 8px;
      transition: background-color 0.3s ease;
    `}
`;

const Arrow = styled.span<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    transform: rotate(${props => (props.$isOpen ? '180deg' : '0deg')});
    transform-origin: center center;
`;

const Menu = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background-color: ${colors.primaryWhite};
  border: 1px solid ${colors.grey6};
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  list-style: none;
  margin: 0;
  z-index: 10;
  min-width: 180px;
`;

const MenuItem = styled.li`
  padding: 12px 24px;
  font-family: ${fonts.fontFamily.headings};
  font-size: 16px;
  font-weight: 600;
  color: ${colors.primaryDarkJungleGreen};
  cursor: pointer;
  border-radius: 100px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.grey5};
  }
`;

const DropdownButton = ({ options, selectedId, onChange }:ComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<number | null>(selectedId);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const optionsMap = useMemo(
    () => new Map(options.map(opt => [opt.id, opt.title])),
    [options]
  );

  const handleSelect = (option: number) => {
    setSelected(option);
    setIsOpen(false);
    onChange?.(option);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Wrapper ref={wrapperRef}>
      <Button onClick={() => setIsOpen(prev => !prev)}>
        {selected !== null ? optionsMap.get(selected) : 'Please select'}
        <Arrow $isOpen={isOpen}><Icon icon={iconArrowDown} size={28}/></Arrow>
      </Button>
      {isOpen && (
        <Menu>
          {options.map(option => (
            <MenuItem key={option.id} onClick={() => handleSelect(option.id)}>
              {option.title}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};

export default DropdownButton;
